import {
  type DesignerSourceFile,
  SITE_FUNCTION_DESCRIPTION_MAX_LENGTH,
  SOURCE_FILE_HARD_MAX_CONTENT_BYTES,
  SOURCE_FILE_MAX_CONTENT_BYTES,
  SOURCE_FILE_WRITE_EXTENSIONS,
  validateRelativePath,
  validateSourceFilePathAndContent,
  type SourceFileWriteViolation,
} from '@packages/domains/code-libraries/data-types';

import {
  PROJECTED_GLOBALS_FILENAME,
  PROJECTED_INDEX_FILENAME,
  PROJECTED_PACKAGE_JSON_FILENAME,
  PROJECTED_THEME_FILENAME,
  WEBFLOW_CONFIG_FILENAME,
} from '../constants';
import {ensureLeadingSlash, stripLeadingSlash} from '../paths';
import {leadingDescriptionExceedsLimit} from '../source-files/description';
import {classifySourceFile} from './components';
import {isSkillFile} from './isProjectedFile';

/** Adds the reason only this layer can detect to the data-types set. */
export type SourceFileWriteRejection =
  | SourceFileWriteViolation
  | 'reserved-path'
  | 'description-too-long';

/**
 * Paths the projector generates. A stored source file under one of these names
 * is shadowed by the projection on every rebuild, so the write silently does
 * nothing — better to refuse it.
 *
 * Note this is NOT `isProjectedFile`: that predicate also returns true for
 * every `*.webflow.tsx`, which is exactly what the agent is supposed to write.
 */
const RESERVED_PROJECTED_PATHS = new Set(
  [
    PROJECTED_PACKAGE_JSON_FILENAME,
    PROJECTED_INDEX_FILENAME,
    PROJECTED_THEME_FILENAME,
    PROJECTED_GLOBALS_FILENAME,
    WEBFLOW_CONFIG_FILENAME,
  ].map(stripLeadingSlash)
);

/**
 * Validates a source file a client wants to persist. Returns the first reason
 * it was refused, or null when it is acceptable.
 *
 * Callers own the wording: the multiplayer store turns a rejection into a
 * socket error, the agent tools turn it into a message the model self-corrects
 * from, and the UI turns it into a toast.
 */
export const validateSourceFileWrite = (input: {
  folderId?: string | null;
  name: string;
  path?: string;
  content?: string;
}): SourceFileWriteRejection | null => {
  const violation = validateSourceFilePathAndContent(input);
  if (violation === 'unsafe-path' || violation === 'control-characters') {
    return violation;
  }

  // `path` is client-supplied and is what sourceFileSandboxPath projects the
  // row onto disk with, so it needs the same rules as `name` -- otherwise
  // {name: 'ok.ts', path: '../../escape.ts'} clears validation.
  if (input.path !== undefined) {
    const pathViolation = validateRelativePath(stripLeadingSlash(input.path));
    if (pathViolation) {
      return pathViolation;
    }
  }

  // Match sourceFileSandboxPath: persisted paths are authoritative, with the
  // folder/name shape as the legacy fallback.
  const sandboxPath = input.path
    ? ensureLeadingSlash(input.path)
    : input.folderId
      ? ensureLeadingSlash(`${input.folderId}/${stripLeadingSlash(input.name)}`)
      : ensureLeadingSlash(input.name);
  const resolvedPath = stripLeadingSlash(sandboxPath);

  // Projected paths live at the sandbox root. The same filename inside a
  // folder is an ordinary user file.
  if (RESERVED_PROJECTED_PATHS.has(resolvedPath) || isSkillFile(resolvedPath)) {
    return 'reserved-path';
  }

  if (violation) return violation;
  // Only function files feed the catalog, so only they are held to its limit.
  if (
    input.content !== undefined &&
    classifySourceFile(resolvedPath) === 'function' &&
    leadingDescriptionExceedsLimit(input.content)
  ) {
    return 'description-too-long';
  }
  return null;
};

/** Human-readable reason, shared by every surface that reports a rejection. */
export const describeSourceFileWriteRejection = (
  rejection: SourceFileWriteRejection,
  name: string
): string => {
  switch (rejection) {
    case 'unsafe-path':
      return `"${name}" is not a valid file path. Use a relative path with no "..", leading slash, or empty segments.`;
    case 'control-characters':
      return `"${name}" contains control characters.`;
    case 'unsupported-extension':
      return `"${name}" has an unsupported file extension. Allowed: ${SOURCE_FILE_WRITE_EXTENSIONS.join(', ')}.`;
    case 'reserved-path':
      return `"${name}" is a reserved path generated by Webflow and cannot be written to.`;
    case 'content-too-large':
      return `"${name}" is larger than the ${SOURCE_FILE_MAX_CONTENT_BYTES / 1024}KB per-file limit. Split it into smaller modules.`;
    case 'content-exceeds-hard-limit':
      return `"${name}" exceeds the ${SOURCE_FILE_HARD_MAX_CONTENT_BYTES / (1024 * 1024)}MB maximum file size.`;
    case 'description-too-long':
      return `"${name}" starts with a block comment longer than ${SITE_FUNCTION_DESCRIPTION_MAX_LENGTH} characters. Shorten that comment.`;
    default: {
      const unexpected: never = rejection;
      return unexpected;
    }
  }
};

/**
 * Rejections the sandbox-commit path enforces, as opposed to warns about.
 *
 * That path promotes committed files to source files with a denylist -- every
 * path that is not projected and not a build artifact -- so it legitimately
 * produces extensions the write allowlist does not name (`.svg`, `.html`,
 * `.mjs`, extension-less files a build emits). Rejecting those there drops
 * them silently: the commit never goes through the agent tools, so nothing
 * validates before the write and nothing reports after it.
 *
 * These four are the ones a build cannot produce by accident, so they stay
 * fatal on every path. Extension and size are still hard errors in the tool
 * executors, where the model reads the reason and corrects itself.
 */
const SYNC_PATH_FATAL_REJECTIONS = new Set<SourceFileWriteRejection>([
  'unsafe-path',
  'control-characters',
  'reserved-path',
  'content-exceeds-hard-limit',
]);

/** Whether a rejection should block a write that reached the sync path. */
export const isFatalOnSyncPath = (
  rejection: SourceFileWriteRejection
): boolean => SYNC_PATH_FATAL_REJECTIONS.has(rejection);

/**
 * Whether a row is code the person editing owns, rather than something the
 * platform or a build produced.
 *
 * Two rejections are exempt for anything else -- a projected path, and an
 * extension outside the write allowlist -- because the platform or a build can
 * legitimately produce both. The sync path applies those writes and counts them
 * `skipped`, so every surface that refuses has to make the same carve-out or it
 * refuses a write the server would take: a `.skills/*` document or a `.mdc`
 * rule, for instance.
 */
export const isUserCodeSourceFile = (
  file: Pick<DesignerSourceFile, 'kind' | 'origin'>
): boolean => file.kind === 'code' && file.origin === undefined;
